// ---------------------------------------------------------------------------
// Pre-award submittals: before a deal is won, pre-sales and sales send submittals to the consultant and revise
// them until approved. Every revision is kept (sent, reply due, reply, code, comments, file). When the deal is
// won, the submittals move to the project's Submittals log.
// ---------------------------------------------------------------------------
import { z } from 'zod';

export const SUBMITTAL_KINDS = [
  { value: 'material', label: 'Material submittal' },
  { value: 'design_drawing', label: 'Design drawing (rack elevation, FOV, riser, layout)' },
  { value: 'shop_drawing', label: 'Shop drawing' },
  { value: 'datasheet', label: 'Technical datasheet' },
  { value: 'calculation', label: 'Calculation / report (battery, storage, bandwidth…)' },
  { value: 'compliance', label: 'Compliance statement' },
  { value: 'method_statement', label: 'Method statement' },
  { value: 'other', label: 'Other' },
] as const;
/** The consultant's codes (the same as on the project's Submittals log). */
export const SUBMITTAL_CODES = [
  { value: 'not_sent', label: 'Not sent' },
  { value: 'under_review', label: 'Under review' },
  { value: 'A', label: 'A – Approved' },
  { value: 'B', label: 'B – Approved with comments' },
  { value: 'C', label: 'C – Revise & resubmit' },
  { value: 'rejected', label: 'Rejected' },
] as const;
export type SubmittalCode = (typeof SUBMITTAL_CODES)[number]['value'];
export const submittalCodeLabel = (c: string) => SUBMITTAL_CODES.find((x) => x.value === c)?.label ?? c;

export interface DealSubmittalRevision {
  id: string;
  revision: string;
  sentDate: string | null;
  replyDue: string | null;
  replyDate: string | null;
  code: string;
  comments: string | null;
  documentId: string | null;
  fileName: string | null;
  fileUrl: string | null;
  /** The consultant's response document. */
  replyDocumentId: string | null;
  replyFileName: string | null;
  replyFileUrl: string | null;
  createdByName: string;
}
export interface DealSubmittal {
  id: string;
  ref: string;
  title: string;
  system: string | null;
  kind: string | null;
  brand: string | null;
  status: string;
  currentRevision: string;
  replyDue: string | null;
  updatedAt: string;
  revisions: DealSubmittalRevision[];
}

const date = z.preprocess((v) => (v === '' ? null : v), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').nullable()).optional();
const text = (max: number) => z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(max).nullable()).optional();

export const dealSubmittalSchema = z.object({
  title: z.string().trim().min(1, 'What is submitted?').max(255),
  system: text(40),
  /** A standard type (SUBMITTAL_KINDS) or one pre-sales added with "+ Type" (its own name). */
  kind: z.string().trim().min(1).max(60).optional(),
  brand: text(120),
  /** The first revision (R0): sent on, reply due, file. Not sent yet when there's no date. */
  sentDate: date,
  replyDue: date,
  documentId: z.string().uuid().nullable().optional(),
}).strict();
export const dealSubmittalUpdateSchema = dealSubmittalSchema.pick({ title: true, system: true, kind: true, brand: true }).partial().strict();
export const submittalRevisionSchema = z.object({ sentDate: date, replyDue: date, documentId: z.string().uuid().nullable().optional() }).strict();
export const submittalReplySchema = z.object({
  code: z.enum(['under_review', 'A', 'B', 'C', 'rejected']),
  replyDate: date,
  comments: text(4000),
  /** The consultant's response file (already uploaded to the deal). */
  documentId: z.string().uuid().nullable().optional(),
}).strict();
